import api from './api';
import type {
  ProDocumentRow, ProDocumentUpdate, ProDocumentUploadResponse,
  DocumentTypeOption, DriverOption, HistoryEntry, ProDocumentFilters,
} from '../types/proDocuments';


function buildListParams(f: ProDocumentFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (f.date_from) params.set('date_from', f.date_from);
  if (f.date_to) params.set('date_to', f.date_to);
  (f.documenttype || []).forEach(t => params.append('documenttype', t));
  if (f.revision) params.set('revision', f.revision);
  return params;
}


/** Numeric order pronumber, or a "T"-prefixed template pronumber ("T110"). */
type Pronumber = number | string;

// --- Live per-leg pay + margin (POST /pros/margin) ---
// Mirrors backend/app/schemas/pro_document.py ProMargin (V1 showmargin read model).

export type PayState = 'OK' | 'MISSING RATE' | 'DO NOT PAY';

export interface LineItem {
  item: string;
  rate: number;
  state: PayState;
  /** For driver-pay line items: true when settled (has a payroll week-ending). */
  isPaid?: boolean;
  /** Week-ending date (MM/DD/YYYY) when isPaid; else "". */
  payrollDate?: string;
  /** Per-row fuel surcharge + commission for driver-pay line items (e.g.
   *  BOBTAIL). fuelPct is the FSC %; blank/0 for customer & additional items. */
  fuelType?: string;
  fuelPct?: number;
  commission?: number;
}

export interface LegAddon {
  item: string;
  rate: number;
  commission: number;
}

export interface DriverPayLeg {
  legnum: number;
  legrecno: number | null;
  rate: number;
  fuelType: string;
  fuelPct: number;
  commission: number;
  addons: LegAddon[];
  legPay: number;
  state: PayState;
  legMiles: number;
  allocatedRevenue: number | null;
  marginPct: number | null;
  isEmployeeCost: boolean;
  /** True when this leg's driver pay is a settled ACTUAL (dp_li has a payroll
   *  date), not a rate-table estimate. Optional for back-compat with older
   *  payloads/fixtures; absent => treat as not paid. */
  isPaid?: boolean;
  /** Formatted payroll settlement date (MM/DD/YYYY) when isPaid; else "". */
  payrollDate?: string;
}

export interface DriverPay {
  legs: DriverPayLeg[];
  lineItems: LineItem[];
  subtotal: number;
  fuel: number;
  commission: number;
  total: number;
}

export interface CustomerPay {
  items: LineItem[];
  total: number;
  fuel: number;
  other: number;
}

export interface AdditionalCosts {
  items: LineItem[];
  total: number;
  fuel: number;
}

export interface MarginRow {
  profitLoss: number;
  marginPct: number;
}

export interface MarginSummary {
  customerCharges: MarginRow;
  fuel: MarginRow;
  total: MarginRow;
  marginLimit: number;
  underLimit: boolean;
}

export interface ProDriverPay {
  pronumber: number;
  driverPay: DriverPay;
  savedMarginPct: number | null;
  marginLimit: number;
}

export interface ProMargin {
  pronumber: number;
  customerPay: CustomerPay;
  driverPay: DriverPay;
  additionalCosts: AdditionalCosts;
  margin: MarginSummary;
}

const proDocumentsService = {
  list: async (pronumber: Pronumber, f: ProDocumentFilters = {}): Promise<ProDocumentRow[]> => {
    const params = buildListParams(f);
    const qs = params.toString();
    const url = qs
      ? `/pros/${pronumber}/documents?${qs}`
      : `/pros/${pronumber}/documents`;
    const r = await api.get<ProDocumentRow[]>(url);
    return r.data;
  },

  upload: async (
    pronumber: Pronumber, file: File, meta: ProDocumentUpdate = {},
  ): Promise<ProDocumentUploadResponse> => {
    const fd = new FormData();
    fd.append('file', file);
    if (meta.documenttype) fd.append('documenttype', meta.documenttype);
    if (meta.description) fd.append('description', meta.description);
    if (meta.driver) fd.append('driver', meta.driver);
    if (meta.forinvoice) fd.append('forinvoice', meta.forinvoice);
    const r = await api.post<ProDocumentUploadResponse>(
      `/pros/${pronumber}/documents`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return r.data;
  },

  edit: async (
    pronumber: Pronumber, mapRecno: number, body: ProDocumentUpdate,
  ): Promise<ProDocumentRow> => {
    const r = await api.patch<ProDocumentRow>(
      `/pros/${pronumber}/documents/${mapRecno}`,
      body,
    );
    return r.data;
  },

  /**
   * Commit the Docs tab's staged Invoice (forinvoice) flags. Called by the ORDER save,
   * not by the grid — V1 ships these with the order (dosaverecord's `documentlist`
   * prefix -> processSaveDocumentsInvoicing) rather than writing each toggle.
   *
   * Unchanged entries are ignored server-side, so the caller may send its whole staged
   * map without filtering.
   */
  setInvoiceFlags: async (
    pronumber: Pronumber,
    flags: { map_recno: number; forinvoice: 'Y' | 'N' }[],
  ): Promise<{ applied: number }> => {
    const r = await api.patch<{ applied: number }>(
      `/pros/${pronumber}/documents/invoice-flags`,
      { flags },
    );
    return r.data;
  },

  /** Soft-delete one document. `source` disambiguates the recno: cmsinvoice_map and
   *  cms_map share the integer space, so a bare recno names two different rows. An
   *  'invoice' delete is additionally gated server-side on `invoice-doc-delete`. */
  remove: async (
    pronumber: Pronumber,
    mapRecno: number,
    source: 'cms' | 'invoice' = 'cms',
  ): Promise<void> => {
    await api.delete(`/pros/${pronumber}/documents/${mapRecno}`, { params: { source } });
  },

  /** Copy-to-All: per-PRO eligibility for copying one doc (reference) to the target PROs. */
  copyToAllPreview: async (
    sourcePronumber: Pronumber, fileinfoRecno: number, pros: (number | string)[],
    signal?: AbortSignal,
  ): Promise<{ rows: Array<{ pronumber: number | string; eligible: boolean; errors: string[] }> }> => {
    const r = await api.post(
      `/orders/change-all/copy-document/preview`,
      { source_pronumber: sourcePronumber, fileinfo_recno: fileinfoRecno, pros },
      { signal },
    );
    return r.data;
  },

  /** Copy-to-All: reference-copy one doc to each target PRO ((nonce, pro)-idempotent). */
  copyToAllApply: async (
    sourcePronumber: Pronumber, fileinfoRecno: number, pros: (number | string)[],
    submitNonce: string,
  ): Promise<{ results: Array<{ pronumber: number | string; applied: boolean; errors: string[] }> }> => {
    const r = await api.post(
      `/orders/change-all/copy-document/apply`,
      { source_pronumber: sourcePronumber, fileinfo_recno: fileinfoRecno, pros, submitNonce },
    );
    return r.data;
  },

  history: async (pronumber: Pronumber, mapRecno: number): Promise<HistoryEntry[]> => {
    const r = await api.get<HistoryEntry[]>(
      `/pros/${pronumber}/documents/${mapRecno}/history`,
    );
    return r.data;
  },

  /**
   * Document-type options.
   *
   * `scope='pro'` (the default) is the PRO universe and feeds the Docs-tab
   * upload/edit pickers, so it must stay limited to types a user may attach to
   * a PRO. `scope='search'` is the Documents search-filter universe, which also
   * covers leg documents (DOCUMENT PHOTO / DOCUMENT SIGNATURE) and INVOICE —
   * types that grid displays but nobody may upload. Keep the two apart.
   */
  documentTypes: async (
    scope: 'pro' | 'search' = 'pro',
  ): Promise<DocumentTypeOption[]> => {
    const r = await api.get<DocumentTypeOption[]>('/document-types', {
      params: { scope },
    });
    return r.data;
  },

  driversForPro: async (pronumber: Pronumber): Promise<DriverOption[]> => {
    const r = await api.get<DriverOption[]>(`/pros/${pronumber}/drivers`);
    return r.data;
  },

  previewBlob: async (
    pronumber: Pronumber, mapRecno: number, source?: 'cms' | 'invoice',
  ): Promise<Blob> => {
    const suffix = source === 'invoice' ? '?source=invoice' : '';
    const r = await api.get<Blob>(
      `/pros/${pronumber}/documents/${mapRecno}/preview${suffix}`,
      { responseType: 'blob' },
    );
    return r.data;
  },

  /**
   * A short-lived SAS URL the BROWSER can fetch itself, carrying the document's real
   * filename in its Content-Disposition. `previewBlob` + `URL.createObjectURL` gives
   * the embedded PDF viewer a nameless blob, so it titles itself with a UUID and saves
   * under that UUID; this is what lets it show and save the real name instead.
   * Per-PRO documents only — Search Results rows come from tables this route cannot
   * address. The URL is a read-only credential for that one blob until it expires.
   */
  previewUrl: async (
    pronumber: Pronumber, mapRecno: number, source?: 'cms' | 'invoice',
  ): Promise<{ url: string; filename: string }> => {
    const suffix = source === 'invoice' ? '?source=invoice' : '';
    const r = await api.get<{ url: string; filename: string }>(
      `/pros/${pronumber}/documents/${mapRecno}/preview-url${suffix}`,
    );
    return r.data;
  },

  downloadBlob: async (
    pronumber: Pronumber, mapRecno: number, source?: 'cms' | 'invoice',
  ): Promise<Blob> => {
    const suffix = source === 'invoice' ? '?source=invoice' : '';
    const r = await api.get<Blob>(
      `/pros/${pronumber}/documents/${mapRecno}/download${suffix}`,
      { responseType: 'blob' },
    );
    return r.data;
  },

  /** Read-only batch driver pay and saved PRO margins; no full margin calculation. */
  getDriverPay: async (pronumbers: number[]): Promise<Record<number, ProDriverPay>> => {
    if (!pronumbers.length) return {};
    const r = await api.post<Record<number, ProDriverPay>>('/pros/driver-pay', { pronumbers });
    return r.data;
  },

  /** Live single-PRO calculation; also refreshes its financial cache. */
  getMargin: async (pronumber: number): Promise<ProMargin> => {
    const r = await api.get<ProMargin>(`/pros/${pronumber}/margin`);
    return r.data;
  },

  /** Live read-only batch calculation. Empty input skips the request. */
  getMargins: async (pronumbers: number[]): Promise<Record<number, ProMargin>> => {
    if (pronumbers.length === 0) return {};
    const r = await api.post<Record<number, ProMargin>>('/pros/margin', { pronumbers });
    return r.data;
  },
};

export default proDocumentsService;
